// src/lib/api/settings/companyBranding.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling } from '../shared/errors'
import { validateRequired } from '../shared/validation'
import { logger } from '../../logger'
import { getBrandingWithDefaults } from '../../defaultBrandingGenerator'

/**
 * Get company branding settings
 * Migrated from supabaseApi.js lines 1986-2053
 */
const _getCompanyBranding = async (companyId, options = {}) => {
  try {
    logger.companyBranding('getCompanyBranding called with companyId:', companyId)
    
    // Ensure we have a valid session before proceeding
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()
    logger.companyBranding('Session check:', { hasSession: !!session, sessionError })
    
    if (!session) {
      logger.companyBranding('No active session found')
      return { data: [] }
    }
    
    // Use provided companyId or get it from context
    const validatedCompanyId = companyId || await getValidatedCompanyId()
    logger.companyBranding('Validated company ID:', validatedCompanyId)
    
    if (!validatedCompanyId) {
      logger.companyBranding('No company ID found')
      return { data: [] }
    }
    
    logger.companyBranding('Getting branding with defaults for company_id:', validatedCompanyId)
    
    // Use the new default branding system
    const branding = await getBrandingWithDefaults(validatedCompanyId, options.template || 'prosperia')
    
    logger.companyBranding('Branding result:', {
      hasData: !!branding,
      brandingId: branding?.id,
      companyName: branding?.company_name,
      template: branding?.template
    })
    
    // Return as array to match expected format in components
    const result = branding ? [branding] : []
    logger.companyBranding('Returning result:', result)
    return { data: result }
  } catch (error) {
    logger.error('[companyBranding] Exception in getCompanyBranding:', error)
    return { data: [] }
  }
}

/**
 * Whitelist payload to avoid unexpected keys/types causing upsert errors
 * Migrated from supabaseApi.js lines 2015-2030
 */
const sanitizeCompanyBrandingPayload = (raw) => {
  if (!raw || typeof raw !== 'object') return {}
  
  const allowed = new Set([
    'logo_url',
    'company_banner_url',
    'company_name',
    'ubi_number',
    'license_number',
    'google_review_url',
    'brand_color',
    'primary_color',
    'accent_color',
    'text_color',
    'selected_palette',
    'header_layout',
    'template',
    'font_heading',
    'font_body',
    'intro_text',
    'outro_text',
    'footer_text',
    // Estimate-specific branding fields
    'estimate_intro_text',
    'estimate_outro_text',
    'estimate_footer_text',
    'estimate_terms_service_type',
    'estimate_terms_use_default',
    'estimate_terms_custom',
    // Invoice-specific branding fields
    'invoice_intro_text',
    'invoice_outro_text',
    'invoice_footer_text',
    'invoice_terms_service_type',
    'invoice_terms_use_default',
    'invoice_terms_custom'
  ])
  
  const sanitized = {}
  for (const [key, value] of Object.entries(raw)) {
    if (!allowed.has(key)) continue
    sanitized[key] = value
  }
  return sanitized
}

/**
 * Ensure the authenticated user's metadata has company_id set to satisfy RLS paths
 * Migrated from supabaseApi.js lines 2032-2050
 */
const ensureUserCompanyMetadata = async (companyId) => {
  try {
    if (!companyId) return

    // 3s race: this metadata sync is non-fatal, and supabase.auth.getUser()
    // can deadlock behind supabase-js's Web Lock after a long tab-out
    // interval. Don't let it block the branding save.
    const { data: { user } } = await Promise.race([
      supabase.auth.getUser(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('auth getUser timeout')), 3000))
    ])
    if (!user) return
    
    const current = user.user_metadata?.company_id
    if (current === companyId) return
    
    await supabase.auth.updateUser({ data: { company_id: companyId } })
    
    // RLS evaluates JWT claims; refresh session so new metadata is in the token
    try {
      await supabase.auth.refreshSession()
    } catch (error) {
      console.warn('[companyBranding] Session refresh failed:', error)
    }
  } catch (error) {
    console.warn('[companyBranding] Failed to update user metadata:', error)
    // Non-fatal: proceed even if metadata update fails; RLS may still pass via employees policy
  }
}

/**
 * Update company branding settings
 * Migrated from supabaseApi.js lines 2054-2142
 */
const _updateCompanyBranding = async (brandingData) => {
  console.log('[companyBranding] updateCompanyBranding called with data:', brandingData)
  
  validateRequired(brandingData, 'Branding data')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  // Make sure auth metadata carries company_id so RLS passes even if employee status isn't active
  await ensureUserCompanyMetadata(companyId)
  
  // Sanitize HTML fields before persisting
  try {
    // Lazy import to avoid circular deps in some bundlers
    const { sanitizeHTML } = await import('../../sanitizeHTML')
    if (brandingData && typeof brandingData === 'object') {
      const copy = { ...sanitizeCompanyBrandingPayload(brandingData) }
      // Sanitize all text fields that may contain HTML
      const textFields = [
        'intro_text', 'outro_text', 'footer_text',
        'estimate_intro_text', 'estimate_outro_text', 'estimate_footer_text',
        'invoice_intro_text', 'invoice_outro_text', 'invoice_footer_text',
        'estimate_terms_custom', 'invoice_terms_custom'
      ]
      textFields.forEach(field => {
        if (typeof copy[field] === 'string') {
          copy[field] = sanitizeHTML(copy[field])
        }
      })
      brandingData = copy
    }
  } catch (error) {
    console.warn('[companyBranding] HTML sanitizer failed to import, proceeding without sanitization:', error)
    // If sanitizer fails to import in some environments, proceed without breaking save
  }

  // Ensure payload is whitelisted even if sanitizer import failed
  brandingData = sanitizeCompanyBrandingPayload(brandingData)

  // Get fresh session and ensure it's valid
  let { data: { session }, error: sessionError } = await supabase.auth.getSession()
  
  // If no session or session is invalid, try to refresh
  if (!session || sessionError) {
    console.log('[companyBranding] No valid session found, attempting to refresh...')
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
    if (refreshError || !refreshData.session) {
      throw new Error('Authentication failed. Please log in again.')
    }
    session = refreshData.session
  }

  if (!session || !session.access_token) {
    throw new Error('No valid authentication token available')
  }

  console.log('[companyBranding] Making branding update request with token:', session.access_token.substring(0, 20) + '...')

  try {
    const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-company-branding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ company_id: companyId, branding: brandingData })
    })
    
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      console.error('[companyBranding] Edge Function failed:', err)
      throw new Error(err.error || `Edge Function failed with status ${resp.status}`)
    }
    
    const result = await resp.json()
    console.log('[companyBranding] updateCompanyBranding success via Edge Function, result:', result)
    return { success: true, data: result }
  } catch (edgeFunctionError) {
    console.warn('[companyBranding] Edge Function failed, falling back to direct database update:', edgeFunctionError.message)
    
    // Fallback to direct database update
    const upsertData = { ...brandingData, company_id: companyId, updated_at: new Date().toISOString() }
    console.log('[companyBranding] Attempting direct database upsert with data:', upsertData)
    
    const { data, error } = await supabase
      .from('company_branding')
      .upsert([upsertData], { 
        onConflict: 'company_id' 
      })
      .select()
    
    if (error) {
      console.error('[companyBranding] Direct database update also failed:', error)
      throw new Error(`Database update failed: ${error.message}`)
    }
    
    console.log('[companyBranding] updateCompanyBranding success via direct database update, result:', data)
    return { success: true, data }
  }
}

// Export functions with error handling
export const getCompanyBranding = withErrorHandling(_getCompanyBranding, 'get company branding', 'company_branding')
export const updateCompanyBranding = withErrorHandling(_updateCompanyBranding, 'update company branding', 'company_branding')